// ============================================================================
// Type Declarations for Highlight API
// ============================================================================

// ============================================================================
// Highlighter Interface
// ============================================================================

interface IHighlighter {
    highlightRanges(ranges: Range[], currentIndex: number): void;
    updateHighlightPositions(currentIndex: number): void;
    clearHighlights(): void;
    destroy(): void;
}

// ============================================================================
// Range Highlighter (using Highlight API)
// ============================================================================

class RangeHighlighter implements IHighlighter {
    private isDestroyed: boolean = false;
    private searchHitHighlight: Highlight;
    private currentSearchHitHighlight: Highlight;

    constructor() {
        this.addHighlightStyles();
        this.searchHitHighlight = new Highlight();
        (CSS as any).highlights.set("search-hit", this.searchHitHighlight);
        this.currentSearchHitHighlight = new Highlight();
        (CSS as any).highlights.set("current-search-hit", this.currentSearchHitHighlight);
    }

    public highlightRanges(ranges: Range[], currentIndex: number): void {
        if (this.isDestroyed) return;

        this.clearHighlights();

        ranges.forEach((range, index) => {
            try {
                if (index === currentIndex) {
                    this.currentSearchHitHighlight.add(range);
                } else {
                    this.searchHitHighlight.add(range);
                }
            } catch (error) {
                console.warn('RangeHighlighter: Failed to create highlight:', error);
            }
        });
    }

    public updateHighlightPositions(): void {
        // Highlight API automatically updates positions, no manual work needed
    }

    public clearHighlights(): void {
        if (this.isDestroyed) return;

        // Clear all highlights from CSS.highlights
        this.searchHitHighlight.clear();
        this.currentSearchHitHighlight.clear();
    }

    public destroy(): void {
        this.isDestroyed = true;
        this.clearHighlights();

        // Remove CSS styles
        const existingStyle = document.getElementById('__search-highlight-styles');
        if (existingStyle) {
            existingStyle.remove();
        }
    }

    private addHighlightStyles(): void {
        // Remove existing styles
        const existingStyle = document.getElementById('__search-highlight-styles');
        if (existingStyle) {
            existingStyle.remove();
        }

        const style = document.createElement('style');
        style.id = '__search-highlight-styles';
        style.textContent = `
        ::highlight(search-hit) {
          background-color: rgba(255, 255, 0, 0.3);
          border: 1px solid rgba(255, 255, 0, 0.6);
        }
        ::highlight(current-search-hit) {
            background-color: rgba(0, 255, 0, 0.3);
            border: 1px solid rgba(0, 255, 0, 0.6);
        }
      `;
        document.head.appendChild(style);
    }
}

// ============================================================================
// Overlay Highlighter
// ============================================================================

class OverlayHighlighter implements IHighlighter {
    private highlightContainer: HTMLElement;
    private isDestroyed: boolean = false;
    private currentRanges: Range[] = [];
    private currentHighlights: Map<string, HTMLElement> = new Map();

    constructor() {
        this.highlightContainer = this.createHighlightContainer();
    }

    private createHighlightContainer(): HTMLElement {
        // Remove existing container
        const existing = document.getElementById('__search-highlights');
        if (existing) {
            existing.remove();
        }

        const container = document.createElement('div');
        container.id = '__search-highlights';
        container.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        pointer-events: none;
        z-index: 1;
        width: 100%;
        height: 100%;
      `;
        document.body.appendChild(container);
        return container;
    }

    public highlightRanges(ranges: Range[], currentIndex: number): void {
        if (this.isDestroyed) return;

        this.currentRanges = ranges;

        // Clear existing highlights but keep the ranges
        this.currentHighlights.clear();
        while (this.highlightContainer.firstChild) {
            this.highlightContainer.removeChild(this.highlightContainer.firstChild);
        }

        ranges.forEach((range, index) => {
            try {
                const rects = range.getClientRects();

                Array.from(rects).forEach((rect, rectIndex) => {
                    if (rect.width > 0 && rect.height > 0) {
                        const highlightId = `__searchhit-${index}-${rectIndex}`;
                        const highlight = this.createHighlightElement(highlightId, rect, index === currentIndex);
                        this.currentHighlights.set(highlightId, highlight);
                        this.highlightContainer.appendChild(highlight);
                    }
                });
            } catch (error) {
                console.warn('Failed to highlight range:', error);
            }
        });
    }

    private createHighlightElement(id: string, rect: DOMRect, isCurrent: boolean): HTMLElement {
        const highlight = document.createElement('div');
        highlight.className = '__searchhit';
        highlight.style.cssText = `
        position: fixed;
        left: ${rect.left}px;
        top: ${rect.top}px;
        width: ${rect.width}px;
        height: ${rect.height}px;
        background: ${isCurrent ? 'rgba(0, 255, 0, 0.3)' : 'rgba(255, 255, 0, 0.3)'};
        border: 1px solid ${isCurrent ? 'rgba(0, 255, 0, 0.6)' : 'rgba(255, 255, 0, 0.6)'};
        pointer-events: none;
        box-sizing: border-box;
      `;
        return highlight;
    }

    public clearHighlights(): void {
        if (this.isDestroyed) return;

        this.currentRanges = [];
        this.currentHighlights.clear();
        while (this.highlightContainer.firstChild) {
            this.highlightContainer.removeChild(this.highlightContainer.firstChild);
        }
    }

    public updateHighlightPositions(currentIndex: number): void {
        if (this.isDestroyed || this.currentRanges.length === 0) return;

        let hasValidUpdates = false;

        // Update positions of existing highlights without recreating them
        this.currentRanges.forEach((range, index) => {
            try {
                // Check if range is still valid
                if (range.startContainer && range.endContainer) {
                    const rects = range.getClientRects();

                    Array.from(rects).forEach((rect, rectIndex) => {
                        const highlightId = `__searchhit-${index}-${rectIndex}`;
                        const highlight = this.currentHighlights.get(highlightId);

                        if (highlight && rect.width > 0 && rect.height > 0) {
                            highlight.style.left = `${rect.left}px`;
                            highlight.style.top = `${rect.top}px`;
                            highlight.style.width = `${rect.width}px`;
                            highlight.style.height = `${rect.height}px`;
                            hasValidUpdates = true;
                        } else {
                            console.log(`Highlight ${highlightId} not found or rect invalid:`, {
                                highlight: !!highlight,
                                rect: { width: rect.width, height: rect.height }
                            });
                        }
                    });
                } else {
                    console.log(`Range ${index} is no longer valid`);
                }
            } catch (error) {
                console.warn('Failed to update highlight positions:', error);
            }
        });

        // If no valid updates were made, force a complete re-highlight
        if (!hasValidUpdates && this.currentRanges.length > 0) {
            this.highlightRanges(this.currentRanges, currentIndex);
        }
    }

    public destroy(): void {
        this.isDestroyed = true;
        if (this.highlightContainer.parentNode) {
            this.highlightContainer.parentNode.removeChild(this.highlightContainer);
        }
    }
}

// ============================================================================
// Highlighter Factory
// ============================================================================

function createHighlighter(): IHighlighter {
    // Check if Highlight API is supported
    if (typeof Highlight !== 'undefined' && (CSS as any).highlights) {
        return new RangeHighlighter();
    } else {
        return new OverlayHighlighter();
    }
}

// Export to make this file a module (required for declare global)
export { createHighlighter, RangeHighlighter, OverlayHighlighter, type IHighlighter };
